import { filterObject } from '@repo/utils';
import { keepPreviousData, useQueries, useQuery } from '@tanstack/react-query';

import { useCheckVerificationsComplete } from 'hooks/useCheckVerificationsComplete';
import http from 'services/http';

import type { RefetchFn } from 'components/util/AwaitQuery';

export const REVENUES_KEY = {
  all: ['revenues'],
  lists: () => [...REVENUES_KEY.all, 'list'],
  list: <T>(filter: T) => [...REVENUES_KEY.lists(), filter],
  cumulative: () => [...REVENUES_KEY.all, 'cumulative'],
  metrics: () => [...REVENUES_KEY.all, 'metric'],
  metric: <T>(filter: T) => [...REVENUES_KEY.lists(), filter],
  topMyAssets: () => [...REVENUES_KEY.all, 'topMyAsset'],
  topMyAsset: <T>(filter: T) => [...REVENUES_KEY.lists(), filter],
  topTotalAssets: () => [...REVENUES_KEY.all, 'topTotalAsset'],
  topTotalAsset: <T>(filter: T) => [...REVENUES_KEY.lists(), filter],
  topAllAsset: <T>(filter: T) => [...REVENUES_KEY.lists(), 'topAllAsset', filter],
};

export interface RevenuesResponseDto {
  cumulativeRevenue: number;
  cumulativeWithdrawal: number;
  previousMonthRevenue: number;
  availableWithdrawal: number;
  revenueShareRate: number;
  cumulativeRevenueFormat: string;
  cumulativeWithdrawalFormat: string;
  previousMonthRevenueFormat: string;
  availableWithdrawalFormat: string;
  revenueShareRateFormat: string;
  availablePreTaxWithdrawal: number; // 세전
  availableAfterTaxWithdrawal: number; // 세후
  availablePreTaxWithdrawalFormat: string;
  availableAfterTaxWithdrawalFormat: string;
  triggerImageShareRate: number;
  triggerImageShareRateFormat?: string;
}

// 수익 정보
const fetchRevenueInformation = async () => {
  const response = await http.get<RevenuesResponseDto>('/affiliate/v1/revenues');
  return response;
};

export const useGetRevenues = () => {
  const isCustomLogin = localStorage.getItem('isCustomLogin') === 'true';
  return useQuery({
    queryKey: REVENUES_KEY.lists(),
    queryFn: fetchRevenueInformation,
    enabled: isCustomLogin,
    staleTime: 1000 * 10,
    refetchOnWindowFocus: true,
  });
};

export interface CumulativeDTO {
  cumulativeRevenue: number;
  cumulativeRevenueFormat: string;
}
// 추후 확장성을 위해 url parameter 처리
const fetchRevenueInformationV2 = async (url: string) => {
  const response = await http.get<CumulativeDTO>(url);
  return response;
};

export const useGetRevenuesCumulative = () => {
  const isCustomLogin = localStorage.getItem('isCustomLogin') === 'true';
  const result = useQuery({
    queryKey: REVENUES_KEY.cumulative(),
    queryFn: () => fetchRevenueInformationV2('/affiliate/v1/revenues/cumulative'),
    enabled: isCustomLogin,
    staleTime: 1000 * 10,
    refetchOnWindowFocus: true,
  });
  return result;
};

export interface Metrics {
  metricDatetime: string; // utc 기준 날짜
  amount: number;
  amountGlobal?: number;
  amountFormat: string;
  amountFormatGlobal?: string;
  preTaxAmount: number;
  preTaxAmountFormat: string;
  preTaxAmountGlobal?: number;
  preTaxAmountGlobalFormat?: string;
  afterTaxAmount: number;
  afterTaxAmountFormat: string;
  afterTaxAmountGlobal?: number;
  afterTaxAmountGlobalFormat?: string;
}

export type SourceType = 'persona' | 'universe' | 'scene';
export type AssetType = 'my' | 'entire';

export interface RevenuesMetricsResponseDto {
  metrics: Metrics[];
  sourceType?: SourceType;
  period: string;
}

export const useGetRevenuesMetrics = (queryParams: { sourceType?: SourceType; period: string }) => {
  const isCustomLogin = localStorage.getItem('isCustomLogin') === 'true';
  const { isAllVerificationCompleted = false } = useCheckVerificationsComplete();
  const enabled = Boolean(isCustomLogin && isAllVerificationCompleted);
  const _queryParams = filterObject<object>(queryParams);

  const results = useQuery({
    queryKey: REVENUES_KEY.metric(_queryParams),
    queryFn: () => http.get<RevenuesMetricsResponseDto>('/affiliate/v1/revenues/metrics', _queryParams),
    placeholderData: keepPreviousData,
    staleTime: 1000 * 10,
    refetchOnWindowFocus: true,
    enabled,
  });
  const isLoading = enabled ? results.isLoading : false;

  return {
    ...results,
    isLoading,
  };
};

export interface RevenuesTopMyAssetsResponseDto {
  rank: number;
  sourceName: string;
  coverImageUrl: string;
}

export const useGetRevenuesTopMyAssets = (sourceType?: SourceType) => {
  const isCustomLogin = localStorage.getItem('isCustomLogin') === 'true';
  const { isAllVerificationCompleted } = useCheckVerificationsComplete();

  return useQuery({
    queryKey: REVENUES_KEY.topMyAsset(sourceType),
    queryFn: () => http.get<RevenuesTopMyAssetsResponseDto[]>('/affiliate/v1/revenues/top/my-assets', { sourceType }),
    enabled: isCustomLogin && isAllVerificationCompleted,
  });
};

export const useGetRevenuesTopTotalAssets = (sourceType?: SourceType) => {
  const isCustomLogin = localStorage.getItem('isCustomLogin') === 'true';
  const { isAllVerificationCompleted } = useCheckVerificationsComplete();

  return useQuery({
    queryKey: REVENUES_KEY.topTotalAsset(sourceType),
    queryFn: () =>
      http.get<RevenuesTopMyAssetsResponseDto[]>('/affiliate/v1/revenues/top/total-assets', { sourceType }),
    enabled: isCustomLogin && isAllVerificationCompleted,
  });
};

// export const useGetRevenuesTopAllAssets = (sourceType?: SourceType) => {
//   return useQuery(
//     REVENUES_KEY.topAllAsset(sourceType),
//     () =>
//       Promise.all([
//         http.get<RevenuesTopMyAssetsResponseDto[]>('/affiliate/v1/revenues/top/total-assets', { sourceType }),
//         http.get<RevenuesTopMyAssetsResponseDto[]>('/affiliate/v1/revenues/top/my-assets', { sourceType }),
//       ]).then(([entire, my]) => ({
//         entire: entire ?? [],
//         my: my ?? [],
//       })),
//     {
//       staleTime: 1000 * 60 * 6, // 6분 동안 캐싱하기
//       suspense: true,
//     }
//   );
// };

export const useGetRevenuesTopAllAssets = (sourceType?: SourceType) => {
  const isCustomLogin = localStorage.getItem('isCustomLogin') === 'true';
  const { isAllVerificationCompleted } = useCheckVerificationsComplete();

  const results = useQueries({
    queries: [
      {
        queryKey: REVENUES_KEY.topAllAsset([sourceType, 'total-assets']),
        queryFn: () =>
          http.get<RevenuesTopMyAssetsResponseDto[]>('/affiliate/v1/revenues/top/total-assets', {
            sourceType: sourceType,
          }),
        // staleTime: 1000 * 60 * 2,
        placeholderData: keepPreviousData,
        enabled: isCustomLogin && isAllVerificationCompleted,
      },
      {
        queryKey: REVENUES_KEY.topAllAsset([sourceType, 'my-assets']),
        queryFn: () =>
          http.get<RevenuesTopMyAssetsResponseDto[]>('/affiliate/v1/revenues/top/my-assets', {
            sourceType: sourceType,
          }),
        // staleTime: 1000 * 60 * 2,
        placeholderData: keepPreviousData,
        enabled: isCustomLogin && isAllVerificationCompleted,
      },
    ],
  });

  const [entireResult, myResult] = results;

  const refetch: RefetchFn = () => {
    if (entireResult.isError) return entireResult.refetch();
    else return myResult.refetch();
  };

  return {
    data: {
      entire: entireResult.data ?? [],
      my: myResult.data ?? [],
    },
    isLoading: entireResult.isLoading && myResult.isLoading,
    isError: entireResult.isError || myResult.isError,
    error: { entire: entireResult.error, my: myResult.error },
    refetch,
    refetchAll: () => Promise.all([entireResult.refetch(), myResult.refetch()]),
    isFetching: !(entireResult.isLoading && myResult.isLoading) && (entireResult.isFetching || myResult.isFetching),
  };
};

// export const useGetRevenuesTopAllAssets = (sourceType?: SourceType) => {
//   const isCustomLogin = localStorage.getItem('isCustomLogin') === 'true';
//   const { isAllVerificationCompleted } = useCheckVerificationsComplete();

//   const results = useQuery(REVENUES_KEY.topAllAsset(sourceType).concat('all'), {
//     queryFn: () =>
//       Promise.allSettled([
//         http.get<RevenuesTopMyAssetsResponseDto[]>('/affiliate/v1/revenues/top/total-assets', {
//           sourceType: sourceType,
//         }),
//         http.get<RevenuesTopMyAssetsResponseDto[]>('/affiliate/v1/revenues/top/my-assets', {
//           sourceType: sourceType,
//         }),
//       ]).then((responses) => {
//         return {
//           entire: responses[0].status === 'fulfilled' ? responses[0].value : [],
//           my: responses[1].status === 'fulfilled' ? responses[1].value : [],
//           errors: responses.filter((res) => res.status === 'rejected').map((res) => res.reason),
//         };
//       }),
//     staleTime: 1000 * 60 * 2,
//     placeholderData: keepPreviousData,
//     enabled: isCustomLogin && isAllVerificationCompleted,
//   });

//   return {
//     data: {
//       entire: results.data?.entire ?? [],
//       my: results.data?.my ?? [],
//     },
//     isLoading: results.isLoading,
//     isError: results.isError,
//     error: results.data?.errors ?? [],
//     refetch: results.refetch,
//     isFetching: results.isFetching,
//   };
// };
